import { FirebaseApp } from 'firebase/app';
import {
  getFirestore,
  collection,
  onSnapshot,
  getDocs,
  getDoc,
  doc,
  writeBatch,
  deleteDoc,
  Firestore,
  QuerySnapshot,
  DocumentData,
} from 'firebase/firestore';
import { CustomWindow } from '../models/global/CustomWindow';
import { LoginHandler } from '../login-handler';
import { signOut } from 'firebase/auth';
import { clearLocalStorage, getLocalStorageWithExpiry, setLocalStorageWithExpiry } from '../common';
import { LocalStorageFields } from '../enums/LocalStorageFields';

/**
 * Represents a base store.
 * @class
 */
export class BaseStore {
  [key: string]: any;
  today = new Date();
  hasBackend = false;
  db!: Firestore;

  constructor(fireApp: FirebaseApp) {
    if (fireApp) {
      this.hasBackend = true; //location.protocol==="https:";
      this.db = getFirestore(fireApp);
    }
  }

  /**
   * Get data from backend system
   * @param {string} entity
   * @param {Function} transformLoad
   * @param {number} ttl cache, default 5 * 60 * 1000
   * @returns
   */
  async getBackendData(entity: string, transformLoad = (x: any) => x, ttl = 5 * 60 * 1000) {
    let localStorageSave = false;
    if (!window.isTest) {
      if (Object.keys(LocalStorageFields).includes(entity)) {
        localStorageSave = true;
      } else {
        if (localStorage.getItem(entity) !== null) {
          localStorage.removeItem(entity);
          console.log(
            `Removed ${entity} from local storage as it is not in LocalStorageFields enum.`
          );
        }
      }
    }

    if (window.isTest || (localStorageSave && this[entity] == null)) {
      const lastEntityUpdatedTime = (window as unknown as CustomWindow).context?.user
        ?.dataUpdatedTime?.[entity];
      const data = getLocalStorageWithExpiry(entity, undefined, lastEntityUpdatedTime, false);
      if (data !== null) this[entity] = transformLoad(data);
    }
    if (this[entity] == null && this.hasBackend) {
      //catch parallel calls
      if (!this[entity + '_promise']) {
        this[entity + '_promise'] = this.getEntities(entity);
      }
      const data = await this[entity + '_promise'];
      //remove promise
      delete this[entity + '_promise'];

      if (data !== null) this[entity] = transformLoad(data);
      if (localStorageSave || window.isTest) setLocalStorageWithExpiry(entity, data, ttl);
    }
    return this[entity];
  }

  /**
   * Get data from backend system. It will call callback function whenever data is changed.
   * @param {string} collectionName
   * @returns {[object]} entities
   */
  async getEntities(collectionName: string) {
    const entities: any[] = [];
    const querySnapshot = await getDocs(collection(this.db, collectionName));
    querySnapshot.forEach((doc: { data: () => any }) => {
      entities.push(doc.data());
    });
    return entities;
  }

  async getBackendSubscribedData(entity: string, callback: Function): Promise<void> {
    if (this.hasBackend) {
      return new Promise((resolve, reject) => {
        this.getEntitiesWithSubscription(entity, (entities: any[]) => {
          this[entity] = entities;
          callback(entities);
          resolve();
        });
      });
    }
    return Promise.resolve();
  }

  /**
   * Get data from backend system with real-time updates.
   * It will call the callback function whenever data is changed.
   * @param {string} collectionName
   * @param {(entities: any[]) => void} callback
   * @returns {() => void} Unsubscribe function
   */
  getEntitiesWithSubscription(
    collectionName: string,
    callback: (entities: any[]) => void
  ): () => void {
    const collectionRef = collection(this.db, collectionName);
    return onSnapshot(collectionRef, (querySnapshot: QuerySnapshot<DocumentData>) => {
      const entities: any[] = [];
      querySnapshot.docChanges().forEach((change) => {
        entities.push({ change: change.doc.data(), type: change.type });
      });
      callback(entities);
    });
  }
  /**
   * Get data from backend system in realtime mode. It will call callback function whenever data is changed.
   * @param {string} collectionName
   * @param {Function} callback
   * @returns {Function} Unsubscribe function. Call this function to stop listening to changes.
   */
  async getEntitiesRealtime(collectionName: string, callback: Function) {
    const entities: any[] = [];
    onSnapshot(collection(this.db, collectionName), (snapshot) => {
      snapshot.forEach((doc: any) => {
        entities.push(doc.data());
      });
      callback(entities);
    });
  }

  /**
   * Get data from backend system in realtime mode. It will call callback function whenever data is changed.
   * @param {string} collectionName
   * @param {string} entityId
   * @param {Function} callback
   * @returns {Function} Unsubscribe function. Call this function to stop listening to changes.
   */
  async getEntityRealtime(collectionName: string, entityId: string, callback: Function) {
    return onSnapshot(doc(this.db, collectionName, entityId), (doc: { data: () => any }) => {
      callback(doc.data());
    });
  }

  /**
   * Get data from backend system
   * @param {string} collectionName
   * @param {string} entityId
   * @returns {object} entity
   */
  async getEntity(collectionName: string, entityId: string) {
    const docRef = doc(this.db, collectionName, entityId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      return null;
    }
  }

  async setEntities(collectionName = '', entities: any[]) {
    // Get a new write batch
    const batch = writeBatch(this.db);
    entities.forEach((entity) => {
      if (!entity.id) entity.id = Math.random().toString(36).substring(2);
      batch.set(doc(this.db, collectionName, entity.id), entity);
    });
    // Commit the batch
    return await batch.commit();
  }

  async logoutHandler(authentication: LoginHandler) {
    if (!authentication.auth) throw new Error('logout not available, no auth found');
    await signOut(authentication.auth);
    location.reload();
  }

  /**
   * Set data to backend system
   * @param {string} collectionName
   * @param {object} entity
   */
  async setEntity(collectionName: string, entity: any) {
    return await this.setEntities(collectionName, [entity]);
  }

  /**
   * Delete data from backend system
   * @param {string} collectionName
   * @param {string} entityId
   */
  async deleteEntity(collectionName: string, entityId: string) {
    return await deleteDoc(doc(this.db, collectionName, entityId));
  }

  dateReg = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/;
  prepareDataForRead(data: any): any {
    if (!data) return data;
    if (Array.isArray(data))
      return data.map((x) => {
        return this.prepareDataForRead(x);
      });
    if (typeof data !== 'object') return data;

    let obj = data;
    Object.keys(obj).forEach((key) => {
      if (typeof obj[key] === 'string') {
        if (
          (obj[key].startsWith('{') && obj[key].endsWith('}')) ||
          (obj[key].startsWith('[') && obj[key].endsWith(']'))
        ) {
          try {
            obj[key] = this.prepareDataForRead(JSON.parse(obj[key]));
            return;
          } catch (e) {
            //do nothing
          }
        }
        if (this.dateReg.test(obj[key])) {
          obj[key] = new Date(obj[key]);
        }
      } else if (typeof obj[key] === 'object') {
        obj[key] = this.prepareDataForRead(obj[key]);
      }
    });
    return obj;
  }

  clearStoreLocalStorage(entity: string) {
    if (entity && this.hasBackend) {
      clearLocalStorage(entity);
      this[entity] = null;
      return;
    }
    else if (this.hasBackend) {
      Object.keys(LocalStorageFields).forEach((key) => {
        clearLocalStorage(key);
        this[key] = null;
      });
    }
  }
}
